import { Button, Tag, Tooltip, Select } from "antd";
import React, { useState } from "react";
import dayjs from "dayjs";
import { NotesIcon, InfoIcon, UploadIcon, DeleteIcon } from "../../components/ActionIcons";
import { FieldLabels, Permissions, getPacsStatusLabel } from "../../constants/fieldLabels";
import { hasPermission, calculateExactAge } from "../../utils/helper";
import { summarizeShortcut, isShortcutActive } from "../../utils/shortcuts";

// Shared ellipsis style for text cells that should truncate with a tooltip.
const ellipsisCell: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
};

// Compact DICOM-style age ("39Y") to match the reference order list. Prefers the
// DOB-derived age (more reliable, matches Dispatch), falling back to the stored
// pat_age (already in "039Y" form).
const formatAgeShort = (rec): string => {
  const exact = calculateExactAge(rec?.patient?.pat_dob); // "39 years" | "3 months" | "5 days"
  if (exact) {
    const y = exact.match(/^(\d+)\s*year/i); if (y) return `${y[1]}Y`;
    const mo = exact.match(/^(\d+)\s*month/i); if (mo) return `${mo[1]}M`;
    const d = exact.match(/^(\d+)\s*day/i); if (d) return `${d[1]}D`;
    return exact;
  }
  const raw = rec?.patient?.pat_age;
  if (raw) {
    const m = String(raw).match(/^0*(\d{1,3})\s*([A-Za-z])?/);
    return m ? `${m[1]}${(m[2] || 'Y').toUpperCase()}` : String(raw);
  }
  return '';
};

// HL7 patient class (stored O / I / E) -> human Reg. Type shown in the reference.
const formatRegType = (v): string => {
  const s = String(v ?? '').trim().toUpperCase();
  if (!s) return '';
  if (s.startsWith('O')) return 'OPD';
  if (s.startsWith('I')) return 'IPD';
  if (s.startsWith('E')) return 'ER';
  return s;
};

const titleCase = (s): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

const formatDateTime = (d): string | null =>
  d ? dayjs(d).format('DD-MM-YYYY HH:mm') : null;



/**
 * Get status badge styling to match PACS page
 */
const getStatusStyle = (status: string) => {
  const baseStyle = {
    height: '20px',
    borderRadius: '4px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '20px',
    letterSpacing: '0.06px',
    verticalAlign: 'middle',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  switch (status) {
    case 'SCANNED':
      return {
        ...baseStyle,
        width: '70px',
        padding: '0 6px 0 6px',
        background: '#DEF0FF',
        color: '#003FD2'
      };
    case 'SIGNEDOFF':
      return {
        ...baseStyle,
        width: '80px',
        padding: '0 6px 0 6px',
        background: '#DAF3EE',
        color: '#00795C'
      };
    case 'DRAFTED':
      return {
        ...baseStyle,
        width: '65px',
        padding: '0 6px 0 6px',
        background: '#F5EFE2',
        color: '#8A6A2E'
      };
    case 'PENDING':
      return {
        ...baseStyle,
        width: '70px',
        padding: '0 6px 0 6px',
        background: '#FEE2E2',
        color: '#991B1B'
      };
    default:
      return {
        ...baseStyle,
        width: 'auto',
        padding: '0 6px 0 6px',
        background: '#DEF0FF',
        color: '#003FD2'
      };
  }
};

export const orderColumns = ({
  canDelete = false,
  onDelete,
  // Technician-only enhancements (Orders page is shown to technicians).
  isTechnician = false,
  addFile,
  viewNotes,
  showOrdDetails,
  assignTechnician,
  userList = [],
  currentUser,
} = {}) => {
  // Technician assignment options: current user pinned to the top as "(Self Assign)",
  // followed by every other technician.
  const currentUsername = currentUser?.username;
  const technicianOptions = [
    ...(currentUsername
      ? [{ label: `${currentUser?.user_fullname || currentUsername} (Self Assign)`, value: currentUsername }]
      : []),
    ...(userList || [])
      .filter(u => u?.user_type === 'technician' && u?.username !== currentUsername)
      .map(u => ({ label: u.user_fullname || u.username, value: u.username })),
  ];

  // Nameless column rendered just before Patient ID with a "show notes" icon.
  const notesIconColumn = {
    key: 'notes_icon',
    title: '',
    width: 25,
    minWidth: 25,
    align: 'center',
    render: (_text, rec) => {
      const hasNotes = rec?.note_id;
      return (
        <Tooltip title={hasNotes ? 'View Notes' : 'No Notes available'}>
          <Button
            type="text"
            size="small"
            disabled={!hasNotes}
            icon={<NotesIcon size={18} disabled={!hasNotes} />}
            onClick={e => {
              e.stopPropagation();
              if (hasNotes && viewNotes) viewNotes(rec);
            }}
          />
        </Tooltip>
      );
    },
  };

  // Nameless column rendered right after the notes icon with a "patient & order
  // info" icon.
  const infoIconColumn = {
    key: 'info_icon',
    title: '',
    width: 25,
    minWidth: 25,
    align: 'center',
    render: (_text, rec) => (
      <Tooltip title="Patient & Order Info">
        <Button
          type="text"
          size="small"
          icon={<InfoIcon size={18} />}
          onClick={e => {
            e.stopPropagation();
            showOrdDetails && showOrdDetails(rec);
          }}
        />
      </Tooltip>
    ),
  };

  // Technician assignment dropdown, placed right after Acc No.
  const technicianColumn = {
    dataIndex: 'assigned_to_tech',
    key: 'assigned_to_tech',
    title: FieldLabels.TECHNICIAN,
    width: 160,
    render: (_text, rec, ind) => {
      const assigned = rec?.order_workflow?.ow_assigned_technician;
      return (
        <Select
          value={assigned || undefined}
          placeholder="Assign technician"
          size="small"
          style={{ width: '100%', minWidth: 150 }}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={technicianOptions}
          onChange={value => assignTechnician && assignTechnician(rec, value, ind)}
          onClick={e => e.stopPropagation()}
        />
      );
    },
  };

  // Actions column for technicians: documentation uploads.
  const technicianActionsColumn = {
    dataIndex: 'actions',
    key: 'actions',
    title: FieldLabels.ACTIONS,
    fixed: 'right',
    width: 56,
    render: (_text, rec) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {hasPermission(Permissions.CREATE_TECH_NOTES) && (
          <Tooltip title="Documentation Uploads">
            <Button
              type="text"
              size="small"
              icon={<UploadIcon size={18} />}
              onClick={e => {
                e.stopPropagation();
                addFile && addFile(rec);
              }}
            />
          </Tooltip>
        )}
      </div>
    ),
  };

  // Column order mirrors the reference RIS order list so every column fits on a
  // normal-width screen without horizontal scroll. Patient Name and Item Desc.
  // flex to absorb spare width; everything else has a tight fixed width. Body
  // Part and the Assigned-To text column were dropped (Body Part is folded into
  // Item Desc., assignment is the technician dropdown). There is no payment field
  // in the schema, so the reference "Payment" column is intentionally omitted.
  return [
    ...(isTechnician ? [notesIconColumn, infoIconColumn] : []),
  {
    dataIndex: 'po_pin',
    key: 'po_pin',
    title: FieldLabels.PATIENT_ID,
    width: 90,
    render: (text, rec) => {
      const patPin = rec?.patient?.pat_pin;
      return (
        <Tooltip title={patPin}>
          <div style={ellipsisCell}>{patPin}</div>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'po_pat_name',
    key: 'po_pat_name',
    title: FieldLabels.PATIENT_NAME,
    flex: 2,
    minWidth: 150,
    render: (text, record) => {
      // Emergency cases are indicated by a reddish row tint (getRowClass),
      // not an inline alert icon next to the patient name.
      const patientName = record.patient?.pat_name;
      return (
        <Tooltip title={patientName}>
          <span className="col-pat-name" style={ellipsisCell}>{patientName}</span>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'patient.pat_age',
    key: 'pat_age',
    title: FieldLabels.PATIENT_REPORT_AGE,
    width: 56,
    align: 'center',
    render: (_text, rec) => formatAgeShort(rec),
  },
  {
    dataIndex: 'patient.pat_sex',
    key: 'pat_sex',
    title: FieldLabels.PATIENT_REPORT_SEX,
    width: 48,
    align: 'center',
    render: (_text, rec) => rec?.patient?.pat_sex,
  },
  {
    dataIndex: 'po_diag_desc',
    key: 'po_diag_desc',
    title: FieldLabels.ITEM_DESCRIPTION,
    flex: 2,
    minWidth: 150,
    render: (txt, rec) => (
      <Tooltip title={rec?.po_diag_desc}>
        <span style={ellipsisCell}>{rec?.po_diag_desc}</span>
      </Tooltip>
    ),
  },
  {
    dataIndex: 'modality',
    key: 'modality',
    title: FieldLabels.MODALITY_SHORT,
    width: 74,
    render: (text, rec) => (
      <span
        style={{
          background: '#F5F5F5',
          border: '1px solid #EAEEF2',
          height: '22px',
          borderRadius: '4px',
          padding: '1px 6px',
          display: 'inline-block',
          lineHeight: '18px',
        }}
      >
        {rec?.po_modality}
      </span>
    ),
  },
  {
    dataIndex: 'po_ref_doc',
    key: 'po_ref_doc',
    title: FieldLabels.REFERRING_PHYSICIAN_SHORT,
    width: 138,
    render: (text, rec) => {
      const refDoc = rec?.po_ref_doc;
      return (
        <Tooltip title={refDoc}>
          <div style={ellipsisCell}>{refDoc}</div>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'po_pat_class',
    key: 'po_pat_class',
    title: FieldLabels.REGISTRATION_TYPE,
    width: 76,
    align: 'center',
    render: (_text, rec) => formatRegType(rec?.po_pat_class),
  },
  {
    dataIndex: 'po_priority',
    key: 'po_priority',
    title: FieldLabels.PRIORITY,
    width: 82,
    render: (_text, rec) => {
      const priority = rec?.po_priority;
      const isUrgent = ['EMERGENCY', 'ASAP'].includes(String(priority || '').toUpperCase());
      return (
        <span style={{ color: isUrgent ? '#991B1B' : '#334155', fontWeight: isUrgent ? 600 : 400 }}>
          {titleCase(priority)}
        </span>
      );
    },
  },
  {
    dataIndex: 'po_ord_no',
    key: 'po_ord_no',
    title: FieldLabels.ORDER_NUMBER,
    width: 94,
    render: (text, rec) => rec?.po_his_ord_no,
  },
  {
    dataIndex: 'po_acc_no',
    key: 'po_acc_no',
    title: FieldLabels.ACCESSION_NUMBER_SHORT,
    width: 102,
    render: (text, rec) => rec?.po_acc_no,
  },
  // Technician assignment dropdown sits right after Acc No (technicians only).
  ...(isTechnician ? [technicianColumn] : []),
  {
    dataIndex: 'po_req_time',
    key: 'sched_order_dt',
    title: FieldLabels.ORDER_DATETIME,
    width: 140,
    render: (_text, rec) => {
      const appt = formatDateTime(rec?.po_appointment_time);
      const ord = formatDateTime(rec?.po_req_time);
      return (
        <div style={{ lineHeight: '13px', fontSize: '11px' }}>
          <div style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{appt || 'Not Scheduled'}</div>
          {ord && <div style={{ color: '#64748b', whiteSpace: 'nowrap' }}>({ord})</div>}
        </div>
      );
    },
  },
  {
    dataIndex: 'po_status',
    key: 'po_status',
    title: FieldLabels.STATUS,
    width: 94,
    render: (text, record) => {
      const pacs_status = record?.order_workflow?.ow_pacs_status;
      return (
        <Tag
          className="custom-status-tag"
          style={getStatusStyle(pacs_status)}
        >
          {getPacsStatusLabel(pacs_status)}
        </Tag>
      );
    },
  },
  // Technician actions column (documentation uploads).
  ...(isTechnician ? [technicianActionsColumn] : []),
  ...(canDelete
    ? [
        {
          dataIndex: 'actions',
          key: 'actions',
          title: FieldLabels.ACTIONS,
          fixed: 'right',
          width: 56,
          render: (_, rec) => (
            <Tooltip title="Delete order">
              <Button
                type="text"
                size="small"
                icon={<DeleteIcon size={18} />}
                onClick={() => onDelete && onDelete(rec)}
              />
            </Tooltip>
          ),
        },
      ]
    : []),
  ].filter((col: any) => !col.hidden);
};


/**
 * Quick-filter chip row. Renders the user's enabled master shortcuts + their own
 * shortcuts (the unified list produced by mergeShortcuts) as uniform neutral
 * pills. `savedFilters` / `handleFilterSelection` are accepted for backward
 * compatibility but no longer rendered (the Saved-views dropdown was retired in
 * favour of the Shortcut Settings dialog).
 */
export const SavedSearches = ({ quickFilters = [], handleQuickFilters, currentFilters = {}, extraChips = null }) => {
  const [flashingKey, setFlashingKey] = useState(null);

  const pillBase: React.CSSProperties = {
    height: '24px',
    padding: '0 10px',
    borderRadius: '6px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '24px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    userSelect: 'none',
  };

  const getPillStyle = (active: boolean, flashing: boolean): React.CSSProperties => {
    if (active || flashing) {
      return {
        ...pillBase,
        background: '#2F6FED',
        border: '1px solid #2F6FED',
        color: '#FFFFFF',
      };
    }
    return {
      ...pillBase,
      background: '#F1F5F9',
      border: '1px solid #E2E8F0',
      color: '#334155',
    };
  };

  if ((!quickFilters || quickFilters.length === 0) && !extraChips) {
    return null;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      {quickFilters && quickFilters.length > 0 && (
        <span style={{ fontWeight: 600, fontSize: '12px', color: '#64748b', marginRight: '2px' }}>
          Shortcuts:
        </span>
      )}

      {quickFilters.map((sc) => {
        const active = isShortcutActive(sc, currentFilters);
        const flashing = flashingKey === sc.key;
        const handleClick = () => {
          handleQuickFilters(sc);
          setFlashingKey(sc.key);
          setTimeout(() => setFlashingKey(null), 700);
        };
        return (
          <Tooltip key={sc.key} title={summarizeShortcut(sc.filterRaw)}>
            <button
              onClick={handleClick}
              style={getPillStyle(active, flashing)}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              {sc.name}
            </button>
          </Tooltip>
        );
      })}
      {extraChips}
    </div>
  );
};

// Old QuickFilters component - COMMENTED OUT - Now using SavedSearches component for quick filters
// This component was at wrong placement, replaced with dynamic quick filters in SavedSearches component
// export const QuickFilters = ({ quickFilters, handleQuickFilters }) => {
//   return (
//     <div className="mb-2">
//       {quickFilters.map((filter, index) => (
//         <span className="quick-filters ml-3" key={filter.qf_name} onClick={() => handleQuickFilters(filter)}>
//           {filter.qf_name}
//         </span>
//       ))}
//     </div>
//   )
// }
